/**
 * Custom Bag Widget JavaScript
 * Handles adding/removing products, calculating totals, and managing the bag experience
 * Converted from jQuery to vanilla JavaScript with improved functionality
 */

class CustomBagWidget {
  constructor(options) {
    this.sectionId = options.sectionId;
    this.discountThreshold = options.discountThreshold || 15000; // Store in cents
    this.discountPercentage = options.discountPercentage || 5;
    this.discountCode = options.discountCode || 'custombag';
    this.currency = options.currency || 'US';
    this.brandColor = options.brandColor || '#ff69b4';
    
    this.section = document.querySelector(`[data-section-id="${this.sectionId}"]`);
    this.allProducts = [];
    this.cartUpdates = {};
    this.totalSum = 0;
    this.isShowingBag = false;
    this.isManuallyRemoving = false; // Flag to prevent sync conflicts during manual removals
    this.priceUpdateTimeout = null; // Debounce mechanism for price updates
    
    // Standard Shopify cart integration (no BOOM dependencies)
    
    this.init();
  }
  
  init() {
    if (!this.section) return;
    
    // Initialize in products view state
    this.section.classList.add('custom-bag-widget--showing-products');
    this.section.classList.remove('custom-bag-widget--showing-bag');
    
    this.bindEvents();
    this.bindScrollEvents();
    this.setupCartSyncEvents();
    this.initializeVariantPrices();
    
    // Load existing cart items after DOM is fully ready
    setTimeout(async () => {
      await this.loadExistingCartItems();
      this.updateDisplay();
      
      // Ensure all button text is properly restored after everything loads
      setTimeout(() => {
        this.allProducts.forEach(product => {
          this.ensureButtonText(product.id);
        });
      }, 200);
    }, 800);
    
    // Initial cart sync check after loading existing items
    setTimeout(() => {
      this.checkCartAndSync();
      
      // Final fallback: ensure ALL buttons have text (not just those in bag)
      this.ensureAllButtonsHaveText();
    }, 1200);
    
    // console.log('Boom Bag Widget initialized with enhanced cart synchronization');
  }
  
  initializeVariantPrices() {
    // Initialize prices for products with variant selectors
    const variantSelects = this.section.querySelectorAll('.custom-bag-widget__variant-select');
    variantSelects.forEach(select => {
      if (select.selectedIndex >= 0) {
        const selectedOption = select.options[select.selectedIndex];
        const productCard = select.closest('.custom-bag-widget__product-card');
        const priceElement = productCard.querySelector('.custom-bag-widget__product-price');
        
        if (selectedOption && priceElement) {
          const price = parseFloat(selectedOption.dataset.price);
          if (!isNaN(price)) {
            priceElement.textContent = `$${price.toFixed(2)}`;
            // console.log('Initialized price for product:', price);
          }
        }
      }
    });
  }
  
  setupCartSyncEvents() {
    // Listen for common cart clearing events
    document.addEventListener('cart:cleared', () => this.handleCartCleared());
    document.addEventListener('cart:updated', (e) => this.handleCartUpdated(e));
    
    // Listen for one-click upsell events
    document.addEventListener('ocu:cart:loaded', () => this.checkCartAndSync());
    document.addEventListener('ocu:cart:updated', () => this.checkCartAndSync());
    document.addEventListener('ocu:cart:cleared', () => this.handleCartCleared());
    
    // Listen for Shopify's cart drawer events
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-clear], .cart-clear, .clear-cart, [href*="/cart/clear"]')) {
        // Small delay to allow cart to clear first
        setTimeout(() => this.checkCartAndSync(), 100);
      }
      
      // Listen for cart remove buttons (both standard cart and one-click upsell)
      if (e.target.matches('cart-remove-button, [data-cart-remove], .cart-remove, .ocu-remove-item, [class*="remove"]')) {
        setTimeout(() => this.checkCartAndSync(), 200);
      }
    });
    
    // Listen for cart form submissions that might change quantities
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart"]')) {
        setTimeout(() => this.checkCartAndSync(), 300);
      }
    });
    
    // Enhanced polling to catch external changes (re-enabled with better frequency)
    setInterval(() => {
      // Skip sync if we're currently processing a manual removal
      if (!this.isManuallyRemoving) {
        this.checkCartAndSync();
      }
    }, 3000); // Check every 3 seconds
    
    // Listen for popstate events (back/forward navigation that might affect cart)
    window.addEventListener('popstate', () => {
      setTimeout(() => this.checkCartAndSync(), 100);
    });
  }
  
  async checkCartAndSync() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // console.log('Cart sync check:', { 
      //   cartItemCount: cart.item_count, 
      //   bagProductCount: this.allProducts.length,
      //   cartItems: cart.items?.map(item => ({ id: item.variant_id, quantity: item.quantity })),
      //   bagItems: this.allProducts.map(item => ({ id: item.variantId, quantity: item.quantity }))
      // });
      
      // If cart is empty but we have products tracked, clear our state
      if (cart.item_count === 0 && this.allProducts.length > 0) {
        // console.log('Cart was cleared externally, syncing widget state');
        this.handleCartCleared();
        return;
      }
      
      // If cart has items, check for individual item changes
      if (cart.items && cart.items.length > 0) {
        const cartVariantIds = new Set(cart.items.map(item => item.variant_id.toString()));
        const bagVariantIds = new Set(this.allProducts.map(item => item.variantId.toString()));
        
        // Find items that are in cart but NOT in bag (new additions)
        const newItemsInCart = cart.items.filter(cartItem => 
          !bagVariantIds.has(cartItem.variant_id.toString())
        );
        
        if (newItemsInCart.length > 0) {
          // console.log('Found new items in cart that are not in bag:', newItemsInCart);
          
          // Add these new items to the bag by calling loadExistingCartItems
          // This will handle the proper product card matching
          await this.loadExistingCartItems();
          return; // loadExistingCartItems will handle the display updates
        }
        
        // Find items that were removed from cart but still in bag
        const removedFromCart = this.allProducts.filter(bagItem => 
          !cartVariantIds.has(bagItem.variantId.toString())
        );
        
        if (removedFromCart.length > 0) {
          // console.log('Items removed from cart, syncing bag:', removedFromCart);
          
          // Remove these items from the bag
          removedFromCart.forEach(item => {
            this.allProducts = this.allProducts.filter(bagItem => 
              bagItem.variantId.toString() !== item.variantId.toString()
            );
            
            // Update the UI for this product
            const productId = item.id;
            this.updateQuantity(productId, 0);
          });
          
          // Update display after syncing
          this.updateDisplay();
          // updateBagPrice is redundant - updateDisplay already handles pricing
        }
        
        // Check for quantity mismatches
        cart.items.forEach(cartItem => {
          const bagItem = this.allProducts.find(bagItem => 
            bagItem.variantId.toString() === cartItem.variant_id.toString()
          );
          
          if (bagItem && bagItem.quantity !== cartItem.quantity) {
            // console.log(`Quantity mismatch for variant ${cartItem.variant_id}: cart=${cartItem.quantity}, bag=${bagItem.quantity}`);
            
            if (cartItem.quantity === 0) {
              // Item was removed from cart
              this.allProducts = this.allProducts.filter(item => 
                item.variantId.toString() !== cartItem.variant_id.toString()
              );
              this.updateQuantity(bagItem.id, 0);
            } else {
              // Update quantity to match cart
              bagItem.quantity = cartItem.quantity;
              this.updateQuantity(bagItem.id, cartItem.quantity);
            }
            
            this.updateDisplay();
            // updateBagPrice is redundant - updateDisplay already handles pricing
          }
        });
      }
    } catch (error) {
      console.error('Error checking cart state:', error);
    }
  }
  
  handleCartCleared() {
    // console.log('Handling cart cleared - resetting widget state');
    this.allProducts = [];
    this.cartUpdates = {};
    this.totalSum = 0;
    this.resetAllButtons();
    this.resetPricingDisplay();
    this.updateDisplay();
  }
  
  handleCartUpdated(event) {
    // Handle cart updates from other parts of the site
    if (event.detail && event.detail.cart) {
      const cart = event.detail.cart;
      if (cart.item_count === 0) {
        this.handleCartCleared();
      }
    }
  }
  
  resetAllButtons() {
    // console.log('Resetting all buttons to "Add to Bag" state');
    
    const productCards = this.section.querySelectorAll('[data-product-id]');
    
    productCards.forEach(card => {
      const quantityEls = card.querySelectorAll('.custom-bag-widget__quantity');
      const addBtns = card.querySelectorAll('.custom-bag-widget__add-btn');
      const removeBtns = card.querySelectorAll('.custom-bag-widget__remove-btn');
      
      // Hide quantity indicators
      quantityEls.forEach(el => {
        el.textContent = '(0)';
        el.style.display = 'none';
      });
      
      // Reset add buttons to "Add to Bag" state
      addBtns.forEach(btn => {
        const addText = btn.querySelector('.custom-bag-widget__add-text');
        const addAnother = btn.querySelector('.custom-bag-widget__add-another');
        
        btn.classList.remove('custom-bag-widget__add-btn--in-bag');
        if (addText) addText.style.display = 'inline';
        if (addAnother) addAnother.style.display = 'none';
      });
      
      // Hide remove buttons
      removeBtns.forEach(btn => {
        btn.style.display = 'none';
      });
    });
  }
  
  resetPricingDisplay() {
    // console.log('Resetting pricing display');
    
    const totalAmountEl = this.section.querySelector('[data-total-amount]');
    const progressTextEl = this.section.querySelector('[data-progress-text]');
    const finalPriceEl = this.section.querySelector('[data-final-price]');
    const checkoutWraps = this.section.querySelectorAll('.custom-bag-widget__checkout');
    const retailTextEl = this.section.querySelector('.custom-bag-widget__total-label');
    const orderPriceHead = this.section.querySelector('.custom-bag-widget__final-price-label');
    const separator2 = this.section.querySelector('.custom-bag-widget__separator--2');
    const shopMoreBtn = this.section.querySelector('[data-shop-more]');
    
    // Reset total amount
    if (totalAmountEl) totalAmountEl.textContent = '$0.00';
    
    // Reset progress text to initial state
    if (progressTextEl) {
      const neededFormatted = this.formatPrice(this.discountThreshold);
      progressTextEl.innerHTML = `You're only <span>${neededFormatted}</span> from saving 5%!`;
    }
    
    // Hide final price elements
    if (finalPriceEl) {
      finalPriceEl.textContent = '$0.00';
      finalPriceEl.style.display = 'none';
    }
    checkoutWraps.forEach(checkoutWrap => {
      if (checkoutWrap) checkoutWrap.style.display = 'none';
    });
    if (orderPriceHead) orderPriceHead.style.display = 'none';
    if (separator2) separator2.style.display = 'none';
    
    // Reset shop more button
    if (shopMoreBtn) shopMoreBtn.textContent = 'Shop More Now';
    
    // Reset retail text
    if (retailTextEl) retailTextEl.textContent = 'Total:';
    
    // Don't reset cart icon here - let it reflect actual cart state
  }
  
  updateCartIcon(quantity) {
    // Update cart count in header - try common cart count selectors
    const cartCountBubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    if (cartCountBubble) {
      cartCountBubble.textContent = quantity;
    }
    
    // Also update any other common cart count selectors as fallback
    const cartCounts = document.querySelectorAll('[data-cart-count], .cart-count, .header-cart-count, .cart-item-count, #cart-count, .cart-count-bubble span');
    cartCounts.forEach(el => {
      if (el) {
        el.textContent = quantity;
        
        // Show/hide cart count bubble based on quantity
        const bubble = el.closest('.cart-count-bubble');
        if (bubble) {
          if (quantity > 0) {
            bubble.classList.remove('hidden');
            bubble.style.display = '';
          } else {
            bubble.classList.add('hidden');
            bubble.style.display = 'none';
          }
        }
      }
    });
  }
  
  async loadExistingCartItems() {
    try {
      // console.log('Loading existing cart items...');
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (cart.items && cart.items.length > 0) {
        // console.log('Found existing cart items:', cart.items);
        
        // REMOVED: Don't skip if products exist - we need to sync properly
        // Instead, be smart about what to add vs update
        
        // Convert cart items to bag products
        cart.items.forEach(cartItem => {
          // Check if this variant is already in the bag
          const existingBagItem = this.allProducts.find(item => 
            item.variantId === cartItem.variant_id.toString()
          );
          
          if (existingBagItem) {
            // Update quantity if different
            if (existingBagItem.quantity !== cartItem.quantity) {
              // console.log(`Updating quantity for variant ${cartItem.variant_id}: ${existingBagItem.quantity} -> ${cartItem.quantity}`);
              existingBagItem.quantity = cartItem.quantity;
            }
            return; // Skip to next item
          }
          
          // Find the product card for this variant
          const productCards = this.section.querySelectorAll('.custom-bag-widget__product-card');
          
          for (const card of productCards) {
            const addBtn = card.querySelector('.custom-bag-widget__add-btn');
            if (!addBtn) continue;
            
            // Check if this variant matches any variant in the product
            const variantSelect = card.querySelector('.custom-bag-widget__variant-select');
            let matchFound = false;
            
            if (variantSelect) {
              // Check variant select options
              for (const option of variantSelect.options) {
                if (option.value == cartItem.variant_id) {
                  // Set the select to this variant
                  variantSelect.value = option.value;
                  
                  this.allProducts.push({
                    id: card.dataset.productId,
                    variantId: cartItem.variant_id.toString(),
                    price: cartItem.price,
                    title: cartItem.product_title,
                    quantity: cartItem.quantity,
                    sizeVariant: option.dataset.variantSize || '1'
                  });
                  
                  // console.log('Added new cart item to bag:', {
                  //   productId: card.dataset.productId,
                  //   variantId: cartItem.variant_id,
                  //   quantity: cartItem.quantity
                  // });
                  
                  matchFound = true;
                  break;
                }
              }
            } else {
              // Check button variant ID
              if (addBtn.dataset.variantId == cartItem.variant_id) {
                this.allProducts.push({
                  id: card.dataset.productId,
                  variantId: cartItem.variant_id.toString(),
                  price: cartItem.price,
                  title: cartItem.product_title,
                  quantity: cartItem.quantity,
                  sizeVariant: addBtn.dataset.variantSize || '1'
                });
                
                // console.log('Added new cart item to bag:', {
                //   productId: card.dataset.productId,
                //   variantId: cartItem.variant_id,
                //   quantity: cartItem.quantity
                // });
                
                matchFound = true;
              }
            }
            
            if (matchFound) break;
          }
        });
        
        // console.log('Synced cart items with bag:', this.allProducts);
        
        // Ensure button text for all products in the bag
        this.allProducts.forEach(product => {
          this.ensureButtonText(product.id);
        });
        
        // Always update display after syncing
        this.updateDisplay();
        // updateBagPrice is redundant - updateDisplay already handles pricing
        
        // Always update bag view to ensure proper styling is set up, even if not currently showing
        this.updateBagView();
      } else {
        // console.log('No existing cart items found');
      }
    } catch (error) {
      console.error('Error loading existing cart items:', error);
    }
  }
  
  bindEvents() {
    // Add to bag buttons
    this.section.addEventListener('click', (e) => {
      // Prevent multiple handlers from triggering
      let handled = false;
      
      if (!handled && (e.target.matches('.custom-bag-widget__add-btn') || e.target.closest('.custom-bag-widget__add-btn'))) {
        this.handleAddToBag(e);
        handled = true;
      }
      
      // Handle variant select changes
      if (!handled && e.target.matches('.custom-bag-widget__variant-select')) {
        this.handleVariantSelectChange(e);
        handled = true;
      }
      
      // Handle remove buttons - use event delegation for dynamically created bag view buttons
      if (!handled && (e.target.matches('.custom-bag-widget__remove-btn') || e.target.closest('.custom-bag-widget__remove-btn'))) {
        const removeBtn = e.target.matches('.custom-bag-widget__remove-btn') ? e.target : e.target.closest('.custom-bag-widget__remove-btn');
        
        // Check if this is a bag view remove button (has variant-id) or product view remove button
        if (removeBtn.dataset.variantId && removeBtn.dataset.productId) {
          // console.log('Handling bag view remove for variant:', removeBtn.dataset.variantId);
          e.preventDefault();
          e.stopPropagation();
          this.handleRemoveFromBagView(removeBtn.dataset.variantId, removeBtn.dataset.productId);
        } else {
          // console.log('Handling product view remove');
          this.handleRemoveFromBag(e);
        }
        handled = true;
      }
      
      // REMOVED: Handle clicking on quantity or "Add Another" text - this was causing duplicates
      // These elements are inside the button, so they're already handled by the button click above
      
      if (!handled && (e.target.matches('.custom-bag-widget__view-bag') || e.target.closest('.custom-bag-widget__view-bag'))) {
        // console.log('View bag button clicked');
        this.showBagView();
        handled = true;
      }
      else if (!handled && (e.target.matches('.custom-bag-widget__shop-more') || e.target.closest('.custom-bag-widget__shop-more'))) {
        // console.log('Shop more button clicked');
        this.showProductsView();
        handled = true;
      }
      
      if (!handled && (e.target.matches('.custom-bag-widget__checkout-btn:not(:disabled)') || e.target.closest('.custom-bag-widget__checkout-btn:not(:disabled)'))) {
        this.proceedToCheckout();
        handled = true;
      }
      
    });

    // Handle variant select changes
    this.section.addEventListener('change', (e) => {
      if (e.target.matches('.custom-bag-widget__variant-select')) {
        this.handleVariantSelectChange(e);
      }
    });
  }
  
  bindScrollEvents() {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.handleStickyHeader();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll);
  }
  
  handleStickyHeader() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const progressBanner = this.section.querySelector('.custom-bag-widget__progress');
    const checkoutWraps = this.section.querySelectorAll('.custom-bag-widget__checkout');
    
    if (scrollTop > 420) {
      progressBanner?.classList.add('custom-bag-widget__progress--fixed');
      checkoutWraps.forEach(checkoutWrap => {
        checkoutWrap?.classList.add('custom-bag-widget__checkout--fixed');
      });
      this.section.classList.add('custom-bag-widget--fixed-header');
    } else {
      progressBanner?.classList.remove('custom-bag-widget__progress--fixed');
      checkoutWraps.forEach(checkoutWrap => {
        checkoutWrap?.classList.remove('custom-bag-widget__checkout--fixed');
      });
      this.section.classList.remove('custom-bag-widget--fixed-header');
    }
  }
  
  handleAddToBag(e) {
    try {
      // Prevent rapid-fire clicks
      e.preventDefault();
      e.stopPropagation();
      
      // console.log('handleAddToBag called');
      const button = e.target.closest('.custom-bag-widget__add-btn') || e.target;
      // console.log('Button found:', button);
      
      // Check if this button is currently being processed
      if (button.dataset.processing === 'true') {
        // console.log('Button already being processed, ignoring click');
        return;
      }
      
      // Mark as processing
      button.dataset.processing = 'true';
      
      const productCard = button.closest('.custom-bag-widget__product-card');
      // console.log('Product card found:', productCard);
      
      if (!productCard) {
        // console.log('No product card found, returning');
        button.dataset.processing = 'false';
        return;
      }
      
      const productId = button.dataset.productId;
      
      // Check if there's a variant selector for this product
      const variantSelect = productCard.querySelector('.custom-bag-widget__variant-select');
      
      let variantId, price, title, variantSize;
      
      if (variantSelect && variantSelect.value) {
        // Use the currently selected variant from the dropdown
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        variantId = selectedOption.value;
        price = parseFloat(selectedOption.dataset.price);
        title = selectedOption.dataset.title;
        variantSize = selectedOption.dataset.variantSize;
        // console.log('Using variant from select:', { variantId, price, title, variantSize });
      } else {
        // Fallback to button data for products without variants
        variantId = button.dataset.variantId;
        price = parseFloat(button.dataset.price);
        title = button.dataset.title;
        variantSize = button.dataset.variantSize;
        // console.log('Using variant from button:', { variantId, price, title, variantSize });
      }
      
      if (!variantId) {
        // console.log('No variant ID found, returning');
        button.dataset.processing = 'false';
        return;
      }
      
      // console.log('Adding to bag:', { productId, variantId, price, title });
      
      this.addProductToBag({
        id: productId,
        variantId: variantId,
        price: price,
        title: title,
        quantity: 1,
        sizeVariant: variantSize
      });
      
      // Clear processing flag after a short delay
      setTimeout(() => {
        button.dataset.processing = 'false';
        this.ensureButtonText(productId);
      }, 500); // Short delay just for processing flag
      
    } catch (error) {
      console.error('Error in handleAddToBag:', error);
      
      // Try to restore button text if something went wrong
      const button = e.target.closest('.custom-bag-widget__add-btn') || e.target;
      const productCard = button?.closest('.custom-bag-widget__product-card');
      const productId = productCard?.dataset.productId;
      
      if (button) {
        button.dataset.processing = 'false';
      }
      
      if (productId) {
        this.ensureButtonText(productId);
        this.showErrorMessage(productId, 'Failed to add item. Please try again.');
      }
    }
  }
  
  handleVariantSelectChange(e) {
    const select = e.target;
    const selectedOption = select.options[select.selectedIndex];
    
    if (!selectedOption || !selectedOption.value) {
      return;
    }
    
    // Find the product card and its elements
    const productCard = select.closest('.custom-bag-widget__product-card');
    const addButton = productCard.querySelector('.custom-bag-widget__add-btn');
    const priceElement = productCard.querySelector('.custom-bag-widget__product-price');
    const productId = productCard.dataset.productId;
    
    // Get the new price from the selected option
    const newPrice = parseFloat(selectedOption.dataset.price);
    
    // console.log('Raw price from option:', selectedOption.dataset.price);
    // console.log('Parsed price:', newPrice);
    
    if (addButton) {
      // Update button with selected variant data
      addButton.dataset.variantId = selectedOption.value;
      addButton.dataset.price = selectedOption.dataset.price;
      addButton.dataset.title = selectedOption.dataset.title;
      addButton.dataset.variantSize = selectedOption.dataset.variantSize;
      
      // Reset the button to "Add to Bag" state when variant changes
      this.updateQuantity(productId, 0);
      // console.log('Button reset to "Add to Bag" state due to variant change');
    }
    
    // Update the displayed price on the product card (price is in cents)
    if (priceElement && !isNaN(newPrice)) {
      const priceInDollars = (newPrice / 100).toFixed(2);
      priceElement.textContent = `$${priceInDollars}`;
      // console.log('Updated price element to:', `$${priceInDollars}`);
    }
    
    // console.log('Variant selection changed:', {
    //   variantId: selectedOption.value,
    //   price: selectedOption.dataset.price,
    //   title: selectedOption.dataset.title,
    //   variantSize: selectedOption.dataset.variantSize,
    //   updatedPrice: `$${newPrice.toFixed(2)}`
    // });
  }


  
  handleRemoveFromBag(e) {
    const button = e.target.closest('.custom-bag-widget__remove-btn');
    const productCard = button.closest('.custom-bag-widget__product-card');
    const productId = productCard.dataset.productId;
    
    // console.log('Removing product from bag:', productId);
    
    // Prevent multiple simultaneous removals
    if (this.isManuallyRemoving) {
      // console.log('Already processing a removal, skipping');
      return;
    }
    
    // Set flag to prevent automatic sync interference
    this.isManuallyRemoving = true;
    
    // Get all variant IDs for this product that need to be removed from cart
    const variantsToRemove = this.allProducts
      .filter(item => item.id === productId)
      .map(item => item.variantId);

    // console.log('Variants to remove from cart:', variantsToRemove);

    // Immediately remove all bag items for this product from DOM to prevent flash
    variantsToRemove.forEach(variantId => {
      const bagItemToRemove = document.querySelector(`.custom-bag-widget__bag-item[data-variant-id="${variantId}"]`);
      if (bagItemToRemove) {
        // Smooth removal animation
        bagItemToRemove.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
        bagItemToRemove.style.opacity = '0';
        bagItemToRemove.style.transform = 'translateX(-20px)';
        
        // Remove from DOM after animation
        setTimeout(() => {
          if (bagItemToRemove.parentNode) {
            bagItemToRemove.parentNode.removeChild(bagItemToRemove);
          }
          
          // After removing all DOM elements, check if bag should show empty state
          setTimeout(() => {
            if (this.allProducts.length === 0) {
              const bagItemsContainer = this.section.querySelector('[data-bag-items]');
              const emptyMessage = this.section.querySelector('[data-empty-message]');
              
              if (bagItemsContainer) {
                bagItemsContainer.innerHTML = ''; // Ensure it's completely clean
              }
              if (emptyMessage) {
                emptyMessage.style.display = 'block';
              }
            }
          }, 50);
        }, 200);
      }
    });

    // Remove from bag state first
    this.allProducts = this.allProducts.filter(item => item.id !== productId);
    
    // console.log('Updated allProducts after removal:', this.allProducts);
    
    // Reset the product button back to "Add to Bag" state
    this.updateQuantity(productId, 0);
    
    // Remove from Shopify cart
    this.removeFromShopifyCart(variantsToRemove).then(() => {
      // console.log('Successfully removed from Shopify cart');
      
      // Update only pricing/progress elements, skip bag view rebuild since DOM is already updated
      this.updateDisplayWithoutBagRebuild();
      
      // Additional safety check - ensure DOM is properly synced with data
      setTimeout(() => {
        if (this.allProducts.length === 0) {
          const bagItemsContainer = this.section.querySelector('[data-bag-items]');
          const remainingItems = bagItemsContainer ? bagItemsContainer.querySelectorAll('.custom-bag-widget__bag-item') : [];
          if (remainingItems.length > 0) {
            console.warn('Detected stale bag items after removal, forcing cleanup');
            bagItemsContainer.innerHTML = '';
            const emptyMessage = this.section.querySelector('[data-empty-message]');
            if (emptyMessage) emptyMessage.style.display = 'block';
          }
        }
      }, 300);
      
      // Clear the flag after a delay to allow updates to complete
      setTimeout(() => {
        this.isManuallyRemoving = false;
      }, 1000); // Reduced timeout
      
      // Dispatch custom event to notify other systems
      document.dispatchEvent(new CustomEvent('boom:bag:item-removed', {
        detail: { 
          productId: productId, 
          variantIds: variantsToRemove 
        }
      }));
    }).catch(error => {
      console.error('Failed to remove from Shopify cart:', error);
      // Update pricing even if cart removal failed, skip bag view rebuild
      this.updateDisplayWithoutBagRebuild();
      
      // Clear the flag even on error
      setTimeout(() => {
        this.isManuallyRemoving = false;
      }, 1000); // Reduced timeout
    });
  }
  
  handleRemoveFromBagView(variantId, productId) {
    try {
      // console.log('Removing item from bag view:', { variantId, productId });
      
      // Prevent multiple simultaneous removals
      if (this.isManuallyRemoving) {
        // console.log('Already processing a removal, skipping');
        return;
      }
      
      // Set flag to prevent automatic sync interference
      this.isManuallyRemoving = true;
      
      // Immediately remove the specific bag item from DOM to prevent flash
      const bagItemToRemove = document.querySelector(`.custom-bag-widget__bag-item[data-variant-id="${variantId}"]`);
      if (bagItemToRemove) {
        // Smooth removal animation
        bagItemToRemove.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
        bagItemToRemove.style.opacity = '0';
        bagItemToRemove.style.transform = 'translateX(-20px)';
        
        // Remove from DOM after animation
        setTimeout(() => {
          if (bagItemToRemove.parentNode) {
            bagItemToRemove.parentNode.removeChild(bagItemToRemove);
          }
          
          // After removing DOM element, check if bag should show empty state
          setTimeout(() => {
            if (this.allProducts.length === 0) {
              const bagItemsContainer = this.section.querySelector('[data-bag-items]');
              const emptyMessage = this.section.querySelector('[data-empty-message]');
              
              if (bagItemsContainer) {
                bagItemsContainer.innerHTML = ''; // Ensure it's completely clean
              }
              if (emptyMessage) {
                emptyMessage.style.display = 'block';
              }
            }
          }, 50);
        }, 200);
      }
      
      // Remove this specific variant from the bag
      this.allProducts = this.allProducts.filter(item => item.variantId !== variantId.toString());
      
      // console.log('Updated allProducts after removal:', this.allProducts);
      
      // Update the product card button state if no variants of this product remain
      const remainingVariants = this.allProducts.filter(item => item.id === productId);
      if (remainingVariants.length === 0) {
        this.updateQuantity(productId, 0);
      }
      
      // Remove from Shopify cart
      this.removeFromShopifyCart([variantId]).then(() => {
        // console.log('Successfully removed variant from Shopify cart');
        
        // Update only pricing/progress elements, skip bag view rebuild since DOM is already updated
        this.updateDisplayWithoutBagRebuild();
        
        // Additional safety check - ensure DOM is properly synced with data
        setTimeout(() => {
          if (this.allProducts.length === 0) {
            const bagItemsContainer = this.section.querySelector('[data-bag-items]');
            const remainingItems = bagItemsContainer ? bagItemsContainer.querySelectorAll('.custom-bag-widget__bag-item') : [];
            if (remainingItems.length > 0) {
              console.warn('Detected stale bag items after removal, forcing cleanup');
              bagItemsContainer.innerHTML = '';
              const emptyMessage = this.section.querySelector('[data-empty-message]');
              if (emptyMessage) emptyMessage.style.display = 'block';
            }
          }
        }, 300);
        
        // Clear the flag after a delay to allow updates to complete
        setTimeout(() => {
          this.isManuallyRemoving = false;
        }, 1000); // Reduced timeout since we're doing fewer operations
        
        // Dispatch custom event to notify other systems
        document.dispatchEvent(new CustomEvent('boom:bag:item-removed', {
          detail: { 
            productId: productId, 
            variantIds: [variantId] 
          }
        }));
        
      }).catch(error => {
        console.error('Failed to remove from Shopify cart:', error);
        // Update pricing even if cart removal failed, skip bag view rebuild
        this.updateDisplayWithoutBagRebuild();
        
        // Clear the flag even on error
        setTimeout(() => {
          this.isManuallyRemoving = false;
        }, 1000); // Reduced timeout
      });
      
    } catch (error) {
      console.error('Error removing item from bag view:', error);
      // Try to refresh the bag view anyway
      this.updateBagView(); // Use updateBagView for consistent styling
      
      // Clear the flag on error
      this.isManuallyRemoving = false;
    }
  }
  
  async removeFromShopifyCart(variantIds) {
    try {
      // Create updates object with 0 quantities for removal
      const updates = {};
      variantIds.forEach(variantId => {
        updates[variantId] = 0;
      });
      
      // console.log('Removing from Shopify cart:', updates);
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates })
      });
      
      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }
      
      const cart = await response.json();
      // console.log('Cart removal response:', cart);
      
      // Update cart count in header
      this.updateCartIcon(cart.item_count);
      
      // Dispatch cart updated event for other systems
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart }
      }));
      
      return cart;
    } catch (error) {
      console.error('Error removing from Shopify cart:', error);
      throw error;
    }
  }
  
  addProductToBag(product) {
    // console.log('addProductToBag called with:', product);
    // console.log('Current allProducts before adding:', this.allProducts);
    
    // Validate product data
    if (!product || !product.id || !product.variantId || !product.price) {
      console.error('Invalid product data:', product);
      this.showErrorMessage(product?.id, 'Invalid product data. Please try again.');
      return;
    }
    
    try {
      // Ensure variantId is a string for consistency
      product.variantId = product.variantId.toString();
      
      // Find if this exact variant already exists in the bag
      const existingIndex = this.allProducts.findIndex(item => 
        item.id === product.id && item.variantId === product.variantId
      );
      
      // console.log('Existing product index:', existingIndex);
      
      if (existingIndex !== -1) {
        // Update existing product quantity
        this.allProducts[existingIndex].quantity += product.quantity;
        // console.log('Updated existing product:', this.allProducts[existingIndex]);
      } else {
        // Add new product
        this.allProducts.push({
          id: product.id,
          variantId: product.variantId,
          price: product.price,
          title: product.title,
          quantity: product.quantity,
          sizeVariant: product.sizeVariant
        });
        // console.log('Added new product:', this.allProducts[this.allProducts.length - 1]);
      }
      
      // console.log('allProducts after adding:', this.allProducts);
      
      // Update displays IMMEDIATELY before cart update
      this.updateDisplay();
      // Note: updateDisplay() already handles updateBagView() if needed, so no duplicate call required
      
      // Show temporary success message BEFORE cart update to avoid async issues
      this.showAddedToBagMessage(product.id);
      
      // Ensure the button text is preserved
      this.ensureButtonText(product.id);
      
      // Update cart (async) - handle errors separately to avoid false error messages
      this.updateCart().then(() => {
        // console.log('Cart update successful');
        
        // Refresh cart icon with actual cart state
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            this.updateCartIcon(cart.item_count);
            // console.log('Updated cart icon with actual count:', cart.item_count);
          })
          .catch(error => console.error('Error fetching cart for icon update:', error));
        
        // Wait a moment for cart update to settle, then sync
        setTimeout(() => {
          try {
            this.checkCartAndSync();
          } catch (syncError) {
            console.error('Sync check failed (non-critical):', syncError);
            // Don't throw - sync failure shouldn't show error to user
          }
        }, 300);
      }).catch(error => {
        console.error('Cart update failed:', error);
        // Only show error if cart update actually failed
        // But don't throw - the item was already added to the bag display
        console.warn('Cart update failed but item was added to bag display');
      });
      
    } catch (error) {
      console.error('Critical error adding product to bag:', error);
      console.error('Error stack:', error.stack);
      console.error('Product data that caused error:', product);
      
      // Try to restore button state in case of error
      this.ensureButtonText(product?.id);
      
      // Show error message to user only for critical errors
      this.showErrorMessage(product?.id, 'Failed to add item to bag. Please try again.');
      
      // Don't re-throw - handle the error here to prevent false error messages
    }
  }
  
  ensureAllButtonsHaveText() {
    try {
      // console.log('Ensuring all buttons have text...');
      const allButtons = this.section.querySelectorAll('.custom-bag-widget__add-btn');
      allButtons.forEach(btn => {
        const addText = btn.querySelector('.custom-bag-widget__add-text');
        
        // If button has no text span or empty text, fix it
        if (!addText || addText.textContent.trim() === '') {
          // console.log('Fixing button with missing text:', btn);
          
          // Remove any existing spans
          btn.querySelectorAll('.custom-bag-widget__add-text, .custom-bag-widget__add-another').forEach(span => span.remove());
          
          // Create fresh text span
          const textSpan = document.createElement('span');
          textSpan.className = 'custom-bag-widget__add-text';
          textSpan.textContent = 'ADD TO BAG';
          textSpan.style.display = 'inline';
          btn.appendChild(textSpan);
        } else if (addText.textContent.trim() !== 'ADD TO BAG') {
          // Fix wrong text content
          addText.textContent = 'ADD TO BAG';
          addText.style.display = 'inline';
        }
        
        // Remove any "Add another" spans
        btn.querySelectorAll('.custom-bag-widget__add-another').forEach(span => span.remove());
        
        // Ensure button doesn't have in-bag class
        btn.classList.remove('custom-bag-widget__add-btn--in-bag');
      });
    } catch (error) {
      console.error('Error ensuring all buttons have text:', error);
    }
  }

  ensureButtonText(productId) {
    try {
      const productCards = this.section.querySelectorAll(`[data-product-id="${productId}"]`);
      productCards.forEach(card => {
        const addBtns = card.querySelectorAll('.custom-bag-widget__add-btn');
        addBtns.forEach(btn => {
          const addText = btn.querySelector('.custom-bag-widget__add-text');
          const addAnother = btn.querySelector('.custom-bag-widget__add-another');
          
          // Ensure addText exists and has correct content
          if (!addText) {
            const textSpan = document.createElement('span');
            textSpan.className = 'custom-bag-widget__add-text';
            textSpan.textContent = 'ADD TO BAG';
            textSpan.style.display = 'inline';
            btn.appendChild(textSpan);
            // console.log('Created missing addText span for product:', productId);
          } else if (addText.textContent.trim() === '' || addText.textContent.trim() !== 'ADD TO BAG') {
            addText.textContent = 'ADD TO BAG';
            addText.style.display = 'inline';
            // console.log('Restored addText content for product:', productId);
          }
          
          // Remove any "Add another" elements - we don't want this functionality
          if (addAnother) {
            addAnother.remove();
          }
          
          // Ensure proper button state
          btn.classList.remove('custom-bag-widget__add-btn--in-bag');
          
          // Fallback: if button is still empty, recreate with simple innerHTML
          if (btn.textContent.trim() === '' || (!btn.querySelector('.custom-bag-widget__add-text'))) {
            // console.log('Button completely empty, recreating innerHTML for product:', productId);
            btn.innerHTML = '<span class="custom-bag-widget__add-text">ADD TO BAG</span>';
          }
        });
      });
    } catch (error) {
      console.error('Error ensuring button text for product', productId, ':', error);
    }
  }

  showAddedToBagMessage(productId) {
    const productCard = this.section.querySelector(`[data-product-id="${productId}"]`);
    
    if (!productCard) return;
    
    // Remove any existing success message
    const existingMessage = productCard.querySelector('.custom-bag-widget__success-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Disable all add buttons for this product during success message
    const addButtons = productCard.querySelectorAll('.custom-bag-widget__add-btn');
    addButtons.forEach(btn => {
      btn.disabled = true;
      btn.style.opacity = '0.5';
      btn.style.cursor = 'not-allowed';
      btn.style.pointerEvents = 'none';
    });
    
    // Create and add success message below the product card
    const successMessage = document.createElement('div');
    successMessage.className = 'custom-bag-widget__success-message';
    successMessage.textContent = 'Item added to Bag';
    successMessage.style.cssText = `
      font-size: 12px;
      color: ${this.brandColor};
      font-weight: 500;
      margin-top: 8px;
      margin-bottom: 4px;
      text-align: center;
      opacity: 1;
      transition: opacity 0.3s ease;
      width: 100%;
      display: block;
    `;
    
    // Add message after the product card content instead of inside actions
    productCard.appendChild(successMessage);
    
    // Remove message and re-enable button after 3 seconds
    setTimeout(() => {
      if (successMessage && successMessage.parentNode) {
        successMessage.style.opacity = '0';
        setTimeout(() => {
          if (successMessage && successMessage.parentNode) {
            successMessage.remove();
          }
        }, 300);
      }
      
      // Re-enable buttons after message disappears
      addButtons.forEach(btn => {
        btn.disabled = false;
        btn.style.opacity = '1';
        btn.style.cursor = 'pointer';
        btn.style.pointerEvents = 'auto';
      });
    }, 2700);
  }

  showErrorMessage(productId, message) {
    if (!productId) return;
    
    const productCard = this.section.querySelector(`[data-product-id="${productId}"]`);
    
    if (!productCard) return;
    
    // Remove any existing error message
    const existingMessage = productCard.querySelector('.custom-bag-widget__error-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create and add error message
    const errorMessage = document.createElement('div');
    errorMessage.className = 'custom-bag-widget__error-message';
    errorMessage.textContent = message;
    errorMessage.style.cssText = `
      font-size: 12px;
      color: #dc3545;
      font-weight: 500;
      margin-top: 8px;
      margin-bottom: 4px;
      text-align: center;
      opacity: 1;
      transition: opacity 0.3s ease;
      width: 100%;
      display: block;
    `;
    
    productCard.appendChild(errorMessage);
    
    // Remove message after 5 seconds
    setTimeout(() => {
      if (errorMessage && errorMessage.parentNode) {
        errorMessage.style.opacity = '0';
        setTimeout(() => {
          if (errorMessage && errorMessage.parentNode) {
            errorMessage.remove();
          }
        }, 300);
      }
    }, 4700);
  }

  
  removeProduct(productId) {
    // console.log('Removing product:', productId);
    // console.log('Before removal:', this.allProducts);
    
    // Remove all variants of this product from the array
    this.allProducts = this.allProducts.filter(item => item.id !== productId);
    
    // console.log('After removal:', this.allProducts);
    
    // Update the product card display
    this.updateQuantity(productId, 0);
    
    this.updateCart();
    // updateBagPrice is redundant - updateDisplay already handles pricing
    this.updateDisplay();
  }
  
  updateCart() {
    try {
      this.cartUpdates = {};
      let quantityIconCart = 0;
      
      // console.log('Updating cart with allProducts:', this.allProducts);
      
      this.allProducts.forEach((item) => {
        // Use the new flat structure: item.variantId and item.quantity  
        this.cartUpdates[item.variantId] = item.quantity;
        quantityIconCart += item.quantity;
        // console.log(`Setting cart update: variant ${item.variantId} = quantity ${item.quantity}`);
      });
      
      // console.log('Cart updates:', this.cartUpdates);
      // console.log('Total quantity for icon:', quantityIconCart);
      
      // Update cart count in header
      this.updateCartIcon(quantityIconCart);
      
      // Update Shopify cart - RETURN the Promise
      // console.log('Sending cart update to Shopify:', this.cartUpdates);
      
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: this.cartUpdates
        })
      })
      .then(response => response.json())
      .then(cart => {
        // console.log('Cart update response:', cart);
        
        // Update all displays with single debounced call to prevent flashing
        this.updateDisplay();
        
        // Dispatch enhanced cart updated events
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { 
            cart: cart,
            source: 'boom-bag',
            updates: this.cartUpdates
          }
        }));
        
        document.dispatchEvent(new CustomEvent('boom:bag:cart-synced', {
          detail: { 
            cart: cart,
            bagProducts: this.allProducts,
            totals: totals
          }
        }));
        
        // Note: Discount will be applied at checkout automatically
        // console.log('Cart updated successfully. Discount qualification:', totals.qualifiesForDiscount);
        
        // Ensure all button texts are preserved after cart update
        this.ensureAllButtonTexts();
        
        // Return cart data for promise chain
        return cart;
        
      })
      .catch(error => {
        console.error('Cart update failed:', error);
        
        // Dispatch error event
        document.dispatchEvent(new CustomEvent('boom:bag:cart-error', {
          detail: { error: error }
        }));
        
        // Try to restore button texts even after error
        this.ensureAllButtonTexts();
        
        // Re-throw error so calling code can handle it
        throw error;
      });
    } catch (error) {
      console.error('Error in updateCart:', error);
      this.ensureAllButtonTexts();
      // Return rejected promise for consistency
      return Promise.reject(error);
    }
  }
  
  ensureAllButtonTexts() {
    try {
      // console.log('Ensuring all button texts are preserved');
      const productCards = this.section.querySelectorAll('.custom-bag-widget__product-card');
      productCards.forEach(card => {
        const productId = card.dataset.productId;
        if (productId) {
          this.ensureButtonText(productId);
        }
      });
    } catch (error) {
      console.error('Error ensuring all button texts:', error);
    }
  }
  
  getProductVariantSize(productId) {
    const productCard = this.section.querySelector(`[data-product-id="${productId}"]`);
    const addBtn = productCard?.querySelector('.custom-bag-widget__add-btn');
    return parseInt(addBtn?.dataset.variantSize) || 1;
  }
  
  calculateTotals() {
    let subtotal = 0;
    let totalItems = 0;
    
    this.allProducts.forEach((item) => {
      const sum = item.price * item.quantity;
      subtotal += sum;
      totalItems += item.quantity;
    });
    
    const qualifiesForDiscount = subtotal >= this.discountThreshold;
    const discount = qualifiesForDiscount ? Math.floor(subtotal / 20) : 0; // 5% discount, rounded down
    const total = subtotal - discount;
    const amountToDiscount = Math.max(0, this.discountThreshold - subtotal);
    
    return {
      subtotal,
      discount,
      total,
      totalItems,
      qualifiesForDiscount,
      amountToDiscount
    };
  }
  
  updateBagPrice() {
    let allPriceQuantity = 0;
    this.allProducts.forEach((item) => {
      const sum = item.price * item.quantity;
      allPriceQuantity += sum;
    });
    this.countTotalBagSum(allPriceQuantity);
  }
  
  countTotalBagSum(totalAmount) {
    const formattedTotal = this.formatPrice(totalAmount);
    const totalAmountEl = this.section.querySelector('[data-total-amount]');
    if (totalAmountEl) {
      totalAmountEl.textContent = formattedTotal;
    }
    
    const sumNeedForDiscount = this.discountThreshold - totalAmount;
    const progressTextEl = this.section.querySelector('[data-progress-text]');
    const finalPriceEl = this.section.querySelector('[data-final-price]');
    const checkoutWraps = this.section.querySelectorAll('.custom-bag-widget__checkout');
    const retailTextEl = this.section.querySelector('.custom-bag-widget__total-label');
    const orderPriceHead = this.section.querySelector('.custom-bag-widget__final-price-label');
    const separator2 = this.section.querySelector('.custom-bag-widget__separator--2');
    
    // console.log('Elements found:', {
    //   finalPriceEl: !!finalPriceEl,
    //   checkoutWrap: !!checkoutWrap,
    //   progressTextEl: !!progressTextEl,
    //   totalAmount: totalAmount,
    //   sumNeedForDiscount: sumNeedForDiscount
    // });
    
    if (sumNeedForDiscount > 0) {
      // Still need more for discount
      const neededFormatted = this.formatPrice(sumNeedForDiscount);
      if (progressTextEl) {
        progressTextEl.innerHTML = `You're only <span>${neededFormatted}</span> from saving 5%!`;
      }
      
      const shopMoreBtn = this.section.querySelector('[data-shop-more]');
      if (shopMoreBtn) {
        shopMoreBtn.textContent = 'Shop More Now';
      }
      
      checkoutWraps.forEach(checkoutWrap => {
        if (checkoutWrap) checkoutWrap.style.display = 'none';
      });
      if (orderPriceHead) orderPriceHead.style.display = 'none';
      if (separator2) separator2.style.display = 'none';
    } else {
      // Qualified for discount
      const discountAmount = Math.floor(totalAmount / 20);
      const finalAmount = totalAmount - discountAmount;
      const discountFormatted = this.formatPrice(discountAmount);
      const finalFormatted = this.formatPrice(finalAmount);
      
      if (retailTextEl) retailTextEl.textContent = 'Retail Price:';
      if (progressTextEl) {
        progressTextEl.innerHTML = `Congrats! You're saving <span>${discountFormatted}</span>`;
      }
      
      const shopMoreBtn = this.section.querySelector('[data-shop-more]');
      if (shopMoreBtn) {
        shopMoreBtn.innerHTML = '<span class="custom-bag-widget__back-smalltext">Save more! Add items to this bag.</span>';
      }
      
      checkoutWraps.forEach(checkoutWrap => {
        if (checkoutWrap) checkoutWrap.style.display = 'block';
      });
      if (orderPriceHead) {
        orderPriceHead.style.display = 'inline-block';
      }
      if (separator2) separator2.style.display = 'inline-block';
      if (finalPriceEl) {
        finalPriceEl.textContent = finalFormatted;
        finalPriceEl.style.display = 'inline-block';
        // console.log('Setting final price to:', finalFormatted);
      }
    }
  }
  
  formatPrice(priceInCents) {
    if (priceInCents <= 0) return '$0.00';
    
    // Convert cents to dollars
    const dollars = (priceInCents / 100).toFixed(2);
    return `$${dollars}`;
  }
  
  updateQuantity(productId, quantity) {
    try {
      // Only update product grid cards, NOT bag view items
      const productCards = this.section.querySelectorAll(`[data-product-id="${productId}"]:not(.custom-bag-widget__bag-item)`);
      
      productCards.forEach(card => {
        const quantityEls = card.querySelectorAll('.custom-bag-widget__quantity');
        const addBtns = card.querySelectorAll('.custom-bag-widget__add-btn');
        const removeBtns = card.querySelectorAll('.custom-bag-widget__remove-btn');
        
        // Hide quantity indicators completely - user requested to remove all counts
        quantityEls.forEach(el => {
          el.style.display = 'none';
        });
        
        // Update add buttons - ALWAYS show "ADD TO BAG"
        addBtns.forEach(btn => {
          const addText = btn.querySelector('.custom-bag-widget__add-text');
          const addAnother = btn.querySelector('.custom-bag-widget__add-another');
          
          // Ensure button has proper text content
          if (!addText || addText.textContent.trim() === '') {
            // Recreate missing text content
            if (!addText) {
              const textSpan = document.createElement('span');
              textSpan.className = 'custom-bag-widget__add-text';
              textSpan.textContent = 'ADD TO BAG';
              btn.appendChild(textSpan);
            } else {
              addText.textContent = 'ADD TO BAG';
            }
          }
          
          // Remove any "Add another" elements - we don't want this functionality
          if (addAnother) {
            addAnother.remove();
          }
          
          // ALWAYS show "ADD TO BAG" text
          btn.classList.remove('custom-bag-widget__add-btn--in-bag');
          if (addText) addText.style.display = 'inline';
        });
        
        // Hide remove buttons - users can remove items from the bag view
        removeBtns.forEach(btn => {
          btn.style.display = 'none';
        });
      });
      
      this.updateBag();
    } catch (error) {
      console.error('Error updating quantity for product', productId, ':', error);
      
      // Fallback: try to restore button text if something went wrong
      const productCards = this.section.querySelectorAll(`[data-product-id="${productId}"]`);
      productCards.forEach(card => {
        const addBtns = card.querySelectorAll('.custom-bag-widget__add-btn');
        addBtns.forEach(btn => {
          if (btn.textContent.trim() === '' || btn.innerHTML.trim() === '') {
            btn.innerHTML = '<span class="custom-bag-widget__add-text">ADD TO BAG</span>';
          }
        });
      });
    }
  }
  
  updateDisplay(immediate = false) {
    // Clear any existing timeout to prevent multiple rapid updates
    if (this.priceUpdateTimeout) {
      clearTimeout(this.priceUpdateTimeout);
    }
    
    const performUpdate = () => {
      const totals = this.calculateTotals();
      
      // Update progress banner
      this.updateProgressBanner(totals);
      
      // Update product cards
      this.updateProductCards();
      
      // Update checkout button
      this.updateCheckoutButton(totals);
      
      // Update bag view if showing
      if (this.isShowingBag) {
        this.updateBagView();
      }
    };
    
    if (immediate) {
      // Immediate update for removal actions to feel more responsive
      performUpdate();
    } else {
      // Debounced update to prevent flashing during additions
      this.priceUpdateTimeout = setTimeout(performUpdate, 50);
    }
  }
  
  updateDisplayWithoutBagRebuild() {
    // Clear any existing timeout to prevent multiple rapid updates
    if (this.priceUpdateTimeout) {
      clearTimeout(this.priceUpdateTimeout);
    }
    
    const totals = this.calculateTotals();
    
    // Update progress banner
    this.updateProgressBanner(totals);
    
    // Update product cards
    this.updateProductCards();
    
    // Update checkout button
    this.updateCheckoutButton(totals);
    
    // Explicitly skip updateBagView() to avoid rebuilding the entire bag
    // The DOM has already been updated with specific item removal
    
    // Check if bag is empty and handle empty state properly
    if (this.allProducts.length === 0) {
      const emptyMessage = this.section.querySelector('[data-empty-message]');
      const bagItemsContainer = this.section.querySelector('[data-bag-items]');
      
      if (emptyMessage) {
        emptyMessage.style.display = 'block';
      }
      
      // Ensure bag container is completely cleared when empty
      if (bagItemsContainer) {
        // Check for any orphaned bag items and remove them
        const remainingBagItems = bagItemsContainer.querySelectorAll('.custom-bag-widget__bag-item');
        if (remainingBagItems.length > 0) {
          console.warn('Found orphaned bag items when bag should be empty, cleaning up:', remainingBagItems.length);
          bagItemsContainer.innerHTML = ''; // Force clean
        }
      }
    }
  }
  
  updateProgressBanner(totals) {
    const totalAmountEl = this.section.querySelector('[data-total-amount]');
    const progressTextEl = this.section.querySelector('[data-progress-text]');
    const finalPriceEl = this.section.querySelector('[data-final-price]');
    const finalPriceLabelEl = this.section.querySelector('.custom-bag-widget__final-price-label');
    const separator2El = this.section.querySelector('.custom-bag-widget__separator--2');
    const checkoutWraps = this.section.querySelectorAll('.custom-bag-widget__checkout');
    const progressInfo = this.section.querySelector('.custom-bag-widget__progress-info');
    
    if (totalAmountEl) {
      totalAmountEl.textContent = `$${(totals.subtotal / 100).toFixed(2)}`;
    }

    // Always keep the progress info visible to maintain layout
    if (progressInfo) progressInfo.style.display = 'block';

    // Hide checkout section if no items in bag, but keep progress info showing
    if (totals.totalItems === 0) {
      checkoutWraps.forEach(checkoutWrap => {
        if (checkoutWrap) checkoutWrap.style.display = 'none';
      });
      return;
    }

    if (progressTextEl) {
      if (totals.qualifiesForDiscount) {
        // Use dollar amount format to match updateBagPrice method
        const discountAmount = Math.floor(totals.subtotal / 20);
        const discountFormatted = this.formatPrice(discountAmount);
        progressTextEl.innerHTML = `Congrats! You're saving <span>${discountFormatted}</span>`;
        progressTextEl.style.color = '#28a745';
        
        // Show final price section and checkout when qualified
        if (finalPriceEl) {
          finalPriceEl.textContent = `$${(totals.total / 100).toFixed(2)}`;
          finalPriceEl.style.display = 'inline';
        }
        if (finalPriceLabelEl) {
          finalPriceLabelEl.style.display = 'inline';
        }
        if (separator2El) separator2El.style.display = 'inline';
        checkoutWraps.forEach(checkoutWrap => {
          if (checkoutWrap) checkoutWrap.style.display = 'block';
        });
      } else {
        const amountFormatted = `$${(totals.amountToDiscount / 100).toFixed(2)}`;
        progressTextEl.innerHTML = `You're only <span>${amountFormatted}</span> from saving ${this.discountPercentage}%!`;
        progressTextEl.style.color = '#ff69b4';
        
        // Hide final price section and checkout when not qualified
        if (finalPriceEl) {
          finalPriceEl.style.display = 'none';
          finalPriceEl.textContent = ''; // Clear content too
        }
        if (finalPriceLabelEl) finalPriceLabelEl.style.display = 'none';
        if (separator2El) separator2El.style.display = 'none';
        checkoutWraps.forEach(checkoutWrap => {
          if (checkoutWrap) checkoutWrap.style.display = 'none';
        });
      }
    }
  }
  
  updateProductCards() {
    // Only update product grid cards, NOT bag view items
    const productCards = this.section.querySelectorAll('.custom-bag-widget__product-card:not(.custom-bag-widget__bag-item)');
    
    productCards.forEach(card => {
      const productId = card.dataset.productId;
      let totalQuantity = 0;
      
      // Calculate total quantity for this product across all variants
      for (const item of this.allProducts) {
        if (item.id === productId) {
          totalQuantity += item.quantity;
        }
      }
      
      // Update quantity display
      const quantityEl = card.querySelector('.custom-bag-widget__quantity');
      const addTextEl = card.querySelector('.custom-bag-widget__add-text');
      const addAnotherEl = card.querySelector('.custom-bag-widget__add-another');
      const removeBtn = card.querySelector('.custom-bag-widget__remove-btn');
      
      if (totalQuantity > 0) {
        if (quantityEl) {
          quantityEl.textContent = `(${totalQuantity})`;
          quantityEl.style.display = 'inline';
        }
        // ALWAYS show "ADD TO BAG" text, never hide it
        if (addTextEl) {
          addTextEl.style.display = 'inline';
          addTextEl.textContent = 'ADD TO BAG';
        }
        // Remove any "Add another" elements
        if (addAnotherEl) addAnotherEl.remove();
        if (removeBtn) removeBtn.style.display = 'block';
      } else {
        if (quantityEl) quantityEl.style.display = 'none';
        if (addTextEl) {
          addTextEl.style.display = 'inline';
          addTextEl.textContent = 'ADD TO BAG';
        }
        // Remove any "Add another" elements
        if (addAnotherEl) addAnotherEl.remove();
        if (removeBtn) removeBtn.style.display = 'none';
      }
    });
  }
  
  updateCheckoutButton(totals) {
    const checkoutBtns = this.section.querySelectorAll('.custom-bag-widget__checkout-btn');
    
    checkoutBtns.forEach(checkoutBtn => {
      if (checkoutBtn) {
        // console.log('Updating checkout button. Total items:', totals.totalItems, 'Qualifies for discount:', totals.qualifiesForDiscount);
        if (totals.qualifiesForDiscount && totals.totalItems > 0) {
          checkoutBtn.disabled = false;
          checkoutBtn.removeAttribute('disabled');
          checkoutBtn.style.opacity = '1';
          checkoutBtn.style.cursor = 'pointer';
          checkoutBtn.style.pointerEvents = 'auto';
          checkoutBtn.classList.remove('disabled');
          // console.log('Checkout button enabled - qualifies for discount');
        } else {
          checkoutBtn.disabled = true;
          checkoutBtn.setAttribute('disabled', 'disabled');
          checkoutBtn.style.opacity = '0.5';
          checkoutBtn.style.cursor = 'not-allowed';
          checkoutBtn.style.pointerEvents = 'none';
          checkoutBtn.classList.add('disabled');
          // console.log('Checkout button disabled - does not qualify for discount yet');
        }
      }
    });
  }
  
  showBagView() {
    // console.log('showBagView called');
    
    // Instead of fighting CSS with !important, add a class to the widget container
    this.section.classList.add('custom-bag-widget--showing-bag');
    this.section.classList.remove('custom-bag-widget--showing-products');
    
    this.isShowingBag = true;
    
    // Hide all success messages when showing bag view
    const successMessages = this.section.querySelectorAll('.custom-bag-widget__success-message');
    successMessages.forEach(msg => {
      msg.style.display = 'none';
    });
    
    this.updateBag();
    
    // console.log('Added bag view class to widget container');
  }
  
  showProductsView() {
    // console.log('showProductsView called');
    
    // Use classes instead of inline styles
    this.section.classList.add('custom-bag-widget--showing-products');
    this.section.classList.remove('custom-bag-widget--showing-bag');
    
    this.isShowingBag = false;
    
    // Show success messages when back to products view
    const successMessages = this.section.querySelectorAll('.custom-bag-widget__success-message');
    successMessages.forEach(msg => {
      msg.style.display = 'block';
    });
    
    // console.log('Added products view class to widget container');
  }
  
  updateBagView() {
    try {
      const bagItemsContainer = this.section.querySelector('[data-bag-items]');
      const emptyMessage = this.section.querySelector('[data-empty-message]');
      
      if (!bagItemsContainer) {
        console.warn('Bag items container not found');
        return;
      }
      
      // console.log('Updating bag view with products:', this.allProducts);
      
      // Clear existing items
      bagItemsContainer.innerHTML = '';
      
      if (this.allProducts.length === 0) {
        if (emptyMessage) emptyMessage.style.display = 'block';
        // console.log('Bag is empty, showing empty message');
        return;
      }
      
      if (emptyMessage) emptyMessage.style.display = 'none';
      
      // Create bag items
      for (const product of this.allProducts) {
        try {
          const bagItem = this.createBagItemElement(product.variantId, product);
          if (bagItem) {
            bagItemsContainer.appendChild(bagItem);
            // console.log('Added bag item for variant:', product.variantId);
          } else {
            console.error('Failed to create bag item for variant:', product.variantId);
          }
        } catch (itemError) {
          console.error('Error creating bag item for product:', product, itemError);
        }
      }
      
      // console.log('Bag view updated successfully');
      
    } catch (error) {
      console.error('Error updating bag view:', error);
    }
  }
  
  createBagItemElement(variantId, item) {
    // Find the original product data and clone the entire product card structure
    const originalCard = this.findOriginalProductCard(variantId, item);
    
    if (!originalCard) {
      console.error('Could not find original product card for variant:', variantId);
      return this.createFallbackBagItem(variantId, item);
    }
    
    // Create a wrapper with proper bag styling
    const bagItemWrapper = document.createElement('div');
    bagItemWrapper.className = 'custom-bag-widget__bag-item-wrapper';
    
    // Clone the entire original product card to preserve all styling, hover effects, and links
    const div = originalCard.cloneNode(true);
    
    // Add bag-specific classes while keeping all original classes
    div.classList.add('custom-bag-widget__bag-item');
    div.dataset.variantId = variantId;
    
    // Safari image loading fix - multiple approaches to ensure proper loading
    const img = div.querySelector('.custom-bag-widget__product-image img');
    if (img && img.src) {
      const originalSrc = img.src;
      
      // Method 1: Force re-render by toggling display
      img.style.display = 'none';
      requestAnimationFrame(() => {
        img.style.display = 'block';
      });
      
      // Method 2: Reset src to force reload (backup)
      setTimeout(() => {
        if (!img.complete || img.naturalHeight === 0) {
          img.src = '';
          img.src = originalSrc;
        }
      }, 10);
    }
    
    // Remove any variant popup references since we don't need them in bag view
    const variantPopup = div.querySelector('.custom-bag-widget__variants-popup');
    if (variantPopup) {
      variantPopup.remove();
    }
    
    // Remove variant selector since it's non-functional in bag view and confusing
    const variantSelect = div.querySelector('.custom-bag-widget__variant-select');
    if (variantSelect) {
      variantSelect.remove();
    }
    
    // Remove variant selector container as well
    const variantSelector = div.querySelector('.custom-bag-widget__variant-selector');
    if (variantSelector) {
      variantSelector.remove();
    }
    
    // Update the product actions to show bag-specific information and remove button
    const actionsContainer = div.querySelector('.custom-bag-widget__product-actions');
    if (actionsContainer) {
      // Keep the original button structure but update content for bag view
      const originalButton = actionsContainer.querySelector('.custom-bag-widget__add-btn');
      
      if (originalButton) {
        // Calculate prices properly (prices are stored in cents)
        const unitPriceInDollars = (item.price / 100).toFixed(2);
        const totalPriceInDollars = ((item.price * item.quantity) / 100).toFixed(2);
        
        // Replace the add button with bag information and remove button
        actionsContainer.innerHTML = `
          <div class="custom-bag-widget__bag-info">
            <div class="custom-bag-widget__quantity-info">
              <span class="custom-bag-widget__quantity-label">IN BAG:</span>
              <span class="custom-bag-widget__quantity">${item.quantity}</span>
            </div>
            ${item.sizeVariant && item.sizeVariant !== '1' ? `<div class="custom-bag-widget__variant-info">Size: ${item.sizeVariant}</div>` : ''}
            <div class="custom-bag-widget__item-total">
              Total: $${totalPriceInDollars}
            </div>
          </div>
          <button class="custom-bag-widget__remove-btn" data-variant-id="${variantId}" data-product-id="${item.id}">
            REMOVE FROM BAG
          </button>
        `;
        
        // NOTE: No need to add event listeners here since we use event delegation in bindEvents()
      }
    }
    
    // Add the product card to the wrapper
    bagItemWrapper.appendChild(div);
    
    return bagItemWrapper;
  }
  
  findOriginalProductCard(variantId, item) {
    // Try to find the original product card that contains this variant
    const productCards = this.section.querySelectorAll('.custom-bag-widget__product-card:not(.custom-bag-widget__bag-item)');
    
    for (const card of productCards) {
      const buttons = card.querySelectorAll('[data-variant-id]');
      for (const button of buttons) {
        if (button.dataset.variantId === variantId) {
          return card;
        }
      }
      
      // Also check variant selects for products with multiple variants
      const selects = card.querySelectorAll('.custom-bag-widget__variant-select option');
      for (const option of selects) {
        if (option.value === variantId) {
          return card;
        }
      }
    }
    
    return null;
  }
  
  createFallbackBagItem(variantId, item) {
    // Fallback method if original card can't be found - must match the same structure as createBagItemElement
    
    // Create a wrapper with proper bag styling (no inline styles - use CSS classes)
    const bagItemWrapper = document.createElement('div');
    bagItemWrapper.className = 'custom-bag-widget__bag-item-wrapper';
    
    // Create the product card div
    const div = document.createElement('div');
    div.className = 'custom-bag-widget__bag-item custom-bag-widget__product-card';
    div.dataset.variantId = variantId;
    div.dataset.productId = item.id;
    
    // Calculate prices properly (prices are stored in cents)
    const unitPriceInDollars = (item.price / 100).toFixed(2);
    const totalPriceInDollars = ((item.price * item.quantity) / 100).toFixed(2);
    
    div.innerHTML = `
      <div class="custom-bag-widget__product-image">
        <img src="/assets/placeholder.jpg" alt="${item.title}" width="300" height="300" loading="lazy">
      </div>
      <div class="custom-bag-widget__product-info">
        <h3 class="custom-bag-widget__product-title">${item.title}</h3>
        <div class="custom-bag-widget__product-price">$${unitPriceInDollars}</div>
      </div>
      <div class="custom-bag-widget__product-actions">
        <div class="custom-bag-widget__bag-info">
          <div class="custom-bag-widget__quantity-info">
            <span class="custom-bag-widget__quantity-label">IN BAG:</span>
            <span class="custom-bag-widget__quantity">${item.quantity}</span>
          </div>
          ${item.sizeVariant && item.sizeVariant !== '1' ? `<div class="custom-bag-widget__variant-info">Size: ${item.sizeVariant}</div>` : ''}
          <div class="custom-bag-widget__item-total">
            Total: $${totalPriceInDollars}
          </div>
        </div>
        <button class="custom-bag-widget__remove-btn" data-variant-id="${variantId}" data-product-id="${item.id}">
          REMOVE FROM BAG
        </button>
      </div>
    `;
    
    // Add the product card to the wrapper
    bagItemWrapper.appendChild(div);
    
    return bagItemWrapper;
  }
  
  async proceedToCheckout() {
    if (this.allProducts.length === 0) return;
    
    const checkoutBtns = this.section.querySelectorAll('.custom-bag-widget__checkout-btn');
    
    // Show loading state
    checkoutBtns.forEach(checkoutBtn => {
      if (checkoutBtn) {
        checkoutBtn.classList.add('loading');
        checkoutBtn.disabled = true;
      }
    });
    
    try {
      // Small delay to ensure cart is updated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check if theme has cart drawer/sidebar functionality
      const cartDrawer = document.querySelector('[data-cart-drawer], .cart-drawer, #cart-drawer');
      const cartDrawerToggle = document.querySelector('[data-cart-drawer-toggle], .cart-drawer-toggle');
      
      if (cartDrawer && cartDrawerToggle) {
        // Theme has cart drawer - open it
        cartDrawerToggle.click();
      } else if (window.theme && window.theme.openCartDrawer) {
        // Some themes have global cart drawer functions
        window.theme.openCartDrawer();
      } else {
        // Fallback to checkout page with discount code
        const totals = this.calculateTotals();
        if (totals.qualifiesForDiscount) {
          // console.log('Proceeding to checkout with discount code:', this.discountCode);
          window.location.href = `/checkout?discount=${this.discountCode}`;
        } else {
          // console.log('Proceeding to checkout without discount - threshold not met');
          window.location.href = '/checkout';
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      // Fallback to regular cart page
      window.location.href = '/cart';
    } finally {
      // Remove loading state
      const checkoutBtns = this.section.querySelectorAll('.custom-bag-widget__checkout-btn');
      checkoutBtns.forEach(checkoutBtn => {
        if (checkoutBtn) {
          checkoutBtn.classList.remove('loading');
          checkoutBtn.disabled = false;
        }
      });
    }
  }
  
  updateBag() {
    // Delegate to updateBagView which has the proper styling implementation
    this.updateBagView();
  }
  

  
  applyDiscountCode(code) {
    // Try to apply discount code to cart
    fetch('/discount/' + code, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    })
    .then(response => {
      if (response.ok) {
        // console.log('Discount code applied:', code);
      } else {
        // If direct application fails, it will be applied via URL parameter at checkout
        // console.log('Discount code will be applied at checkout:', code);
      }
    })
    .catch(() => {
      // Silently fail - discount will be applied at checkout via URL parameter
      // console.log('Discount code will be applied at checkout:', code);
    });
  }
  
  showVariantPopup(productId) {
    const popup = this.container.querySelector(`[data-variants-popup="${productId}"]`);
    if (popup) {
      popup.style.display = 'flex';
    }
  }
  
  hideAllPopups() {
    const popups = this.container.querySelectorAll('.custom-bag-widget__variants-popup');
    popups.forEach(popup => {
      popup.style.display = 'none';
    });
  }
  

}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Will be initialized by the section template with specific options
});